import { useAuth } from '@/lib/contexts/auth-context';

/**
 * Hook de permissions RBAC.
 *
 * Exploite la map `user.permissions` renvoyée par GET /api/user :
 *   { PERMISSION_CODE: ['SELF', 'DESCENDANTS'], ... }
 *
 * Fournit :
 *  - can(code)                 → le user possède cette permission (peu importe le scope)
 *  - canOnSelf(code)           → permission avec scope SELF
 *  - canOnDescendants(code)    → permission avec scope DESCENDANTS
 *  - permissions                → la map brute
 */
export function usePermissions() {
    const { user } = useAuth();
    const permissions = user?.permissions ?? {};
    const isSuperAdmin = user?.role === 'admin';

    /**
     * Vérifie si l'utilisateur possède la permission `code`.
     * Si `scope` est fourni, vérifie aussi que le scope est inclus.
     * Le Super Admin (role admin) a toutes les permissions.
     */
    const can = (code, scope = null) => {
        if (isSuperAdmin) return true;
        const scopes = permissions[code];
        if (!scopes || !Array.isArray(scopes) || scopes.length === 0) return false;
        if (scope) return scopes.includes(scope);
        return true;
    };

    /** Permission avec scope SELF (entreprise propre) */
    const canOnSelf = (code) => can(code, 'SELF');

    /** Permission avec scope DESCENDANTS (sous-comptes) */
    const canOnDescendants = (code) => can(code, 'DESCENDANTS');

    /** MANAGE_PERMISSIONS + rôle manager + au moins une autre permission modifiable dans ce scope */
    const hasEditablePermissions = (scope) => {
        if (isSuperAdmin) return true;
        const role = user?.role;
        if (role !== 'owner' && role !== 'head_department') return false;
        if (!can('MANAGE_PERMISSIONS', scope)) return false;
        return Object.entries(permissions).some(
            ([code, scopes]) => code !== 'MANAGE_PERMISSIONS' && Array.isArray(scopes) && scopes.includes(scope),
        );
    };

    return { can, canOnSelf, canOnDescendants, hasEditablePermissions, permissions, isSuperAdmin };
}
